import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Liste sayfalarında filtre/arama state'ini URL query param'a bağlar.
 *
 * - Mount'ta URL'den okur; param yoksa `defaultValue`.
 * - `setValue` local state'i ANINDA günceller (responsive input) ve URL'i
 *   `replace: true` ile yazar (history spam yok; geri tuşu harf harf gezmez).
 * - Metin alanları için `debounceMs` verilebilir (URL yazımı geciktirilir, input anlık).
 * - Değer `defaultValue`/boş olunca param URL'den SİLİNİR (temiz URL).
 * - URL dışarıdan değişirse (detaydan geri navigasyon) local state senkronlanır.
 *
 * @param {string} key  URL query anahtarı (ör. 'arama', 'tip')
 * @param {string} defaultValue
 * @param {{debounceMs?: number}} [opts]
 * @returns {[string, (v: string) => void]}
 */
export default function useUrlState(key, defaultValue = '', { debounceMs = 0 } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.has(key) ? searchParams.get(key) : defaultValue;

  const [local, setLocal] = useState(urlValue);
  const lastUrlRef = useRef(urlValue);

  // URL dışarıdan değişince (geri navigasyon / başka kaynak) local'ı senkronla.
  useEffect(() => {
    if (urlValue !== lastUrlRef.current) {
      lastUrlRef.current = urlValue;
      setLocal(urlValue);
    }
  }, [urlValue]);

  const writeUrl = useCallback((val) => {
    lastUrlRef.current = val;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val === defaultValue || val === '' || val === null || val === undefined) {
        next.delete(key);
      } else {
        next.set(key, String(val));
      }
      return next;
    }, { replace: true });
  }, [key, defaultValue, setSearchParams]);

  const timerRef = useRef();
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const setValue = useCallback((val) => {
    setLocal(val); // anlık (input responsive)
    if (debounceMs > 0) {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => writeUrl(val), debounceMs);
    } else {
      writeUrl(val);
    }
  }, [debounceMs, writeUrl]);

  return [local, setValue];
}
